import classNames from 'classnames';
import {useFormikContext} from 'formik';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import ImageCropper from '../../../../../components/ImageCropper';
import usePostWriteImageInStorage from '../../../../../services/hookService/postwriteImageInStorage/usePostWriteImageInStorage';
import {RootState} from '../../../../../store/redux';
import {
  addingCropedImage,
  changeFlag,
  deleteImage,
  IAdminImagesAdvertisement,
} from '../../../../../store/redux/addPictureAdvertisment';
import {ScrollSection} from '../../../../../utilities/helper/scrollToError/errorScroll.constants';
import {ICropedImageAdvertisement} from './../../../../../store/redux/addPictureAdvertisment/index';
import ImageModal from '../../../../../components/mazraeKit/ImageModal';

interface IFormValues {
  hasImages: boolean;
  selectedMainImg: string | number;
}

function AdvertisementUploadImage({onClick}: {onClick: () => void}) {
  const dispatch = useDispatch();
  const {setFieldValue, errors, touched} = useFormikContext<IFormValues>();
  const [openImageModal, setOpenImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const {cropedImage, adminImages} = useSelector(
    (state: RootState) => state.addPictureAdvertisement,
  );

  const dataToRender = [...(cropedImage || []), ...(adminImages || [])];

  const handleAfterSuccess = (imageId?: number, file?: File) => {
    if (file) {
      dispatch(addingCropedImage({file: file, id: imageId, flag: false}));
      onClick(); // Execute the passed onClick handler after successful crop/add
    }
  };

  const {mutate, writeInImageStorageIsLoading} =
    usePostWriteImageInStorage(handleAfterSuccess);

  const handleCropComplete = (croppedFile: File) => {
    if (croppedFile.size >= 10 * 1024 * 1024) {
      toast.error('حجم تصویر نباید بیشتر از 10 مگابایت باشد.');
      return;
    }

    mutate(
      {image: croppedFile},
      {
        onSuccess: data => handleAfterSuccess(data.image_id, croppedFile),
      },
    );
  };

  const handlePreviewImage = (
    item: ICropedImageAdvertisement | IAdminImagesAdvertisement,
  ) => {
    const imageUrl = item.file
      ? URL.createObjectURL(item.file)
      : item.link
      ? item.link
      : '';

    if (!imageUrl) return;
    setOpenImageModal(true);
    setImageUrl(imageUrl);

    // Only revoke object URLs created from File
    if (item.file) {
      setTimeout(() => URL.revokeObjectURL(imageUrl), 1000);
    }
  };

  // this useEffect runs every time the main form page is rendered.
  useEffect(() => {
    if (!dataToRender?.length) return;

    const sortedDataToRender = dataToRender.sort((a, b) => {
      const aMod = a.lastModified ?? a.file?.lastModified ?? 0;
      const bMod = b.lastModified ?? b.file?.lastModified ?? 0;

      return aMod - bMod; // ascending → newest LAST
    });

    dispatch(changeFlag());
    setFieldValue(
      'selectedMainImg',
      Number(sortedDataToRender[sortedDataToRender.length - 1]?.id),
    );
    setFieldValue('hasImages', true);
  }, [dataToRender?.length]);

  return (
    <section id={ScrollSection.IMAGE}>
      <div>
        <span className="font-Medium text-md text-neutral-600">عکس آگهی</span>
        <p className="mt-2 mb-2 font-normal text-md text-zinc-500 [word-spacing:0.25rem] leading-loose">
          با افزودن عکس از محصول یا مزرعه خود بازدید آگهی را افزایش دهید.
        </p>

        <div
          className={classNames({
            'opacity-50 pointer-events-none': writeInImageStorageIsLoading,
          })}
        >
          <ImageCropper onCropComplete={handleCropComplete} />
          {writeInImageStorageIsLoading && (
            <span className="block mt-2 text-sm text-blue-500">
              در حال بارگذاری تصویر...
            </span>
          )}
        </div>
      </div>

      {((cropedImage?.length ?? 0) > 0 || (adminImages?.length ?? 0) > 0) && (
        <div className="flex justify-between items-center my-4 w-10/12">
          <span className="flex text-md">تصاویر بارگذاری شده</span>
          <span className="text-sm">تصویر کاور آگهی</span>
        </div>
      )}

      {/* ----------------------- RENDER REDUX IMAGES ----------------------- */}
      <div className="flex flex-col gap-3 pb-2 mt-2">
        {dataToRender?.map(
          (
            item: ICropedImageAdvertisement | IAdminImagesAdvertisement,
            index: number,
          ) => {
            return (
              <div className="flex justify-between" key={index}>
                <div className="flex">
                  <div
                    className="flex overflow-hidden gap-2 w-20 h-20 rounded-xl cursor-pointer"
                    onClick={() => handlePreviewImage(item)}
                  >
                    <img
                      src={
                        item.file
                          ? URL.createObjectURL(item.file)
                          : item.link
                          ? item.link
                          : ''
                      }
                      className="object-cover w-full h-full"
                      alt=""
                    />
                  </div>
                  <div className="flex flex-col mr-2 text-gray-600">
                    <span className="text-sm">
                      {item.file
                        ? item?.file.name.substring(0, 20)
                        : `Pre-selected Image.jpg`}
                    </span>
                    <span className="text-xs">
                      {item.file
                        ? `${(item?.file.size / 1024).toFixed(2)}k`
                        : 'Default image'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div
                    className="flex justify-center items-center w-11 h-11 bg-green-50 cursor-pointer"
                    onClick={() => {
                      dispatch(changeFlag(index));
                      setFieldValue('selectedMainImg', item.id);
                    }}
                  >
                    <i
                      className={classNames('text-xl text-green-main ', {
                        'icon-nameflag1 text-green-main': item.flag,
                        'icon-nameflag ': !item.flag,
                      })}
                    />
                  </div>
                  <div
                    className="flex justify-center items-center p-2 w-11 h-11 cursor-pointer bg-warning-50"
                    onClick={() => dispatch(deleteImage(index))}
                  >
                    <i className="text-xl text-red-500 icon-nameDelete" />
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
      {(errors?.selectedMainImg || touched?.selectedMainImg) && (
        <span className="text-sm text-red-500">{errors.selectedMainImg}</span>
      )}
      <ImageModal
        images={[imageUrl]}
        isOpen={openImageModal}
        setIsOpen={setOpenImageModal}
        onClose={() => setImageUrl('')}
      />
    </section>
  );
}

export default AdvertisementUploadImage;
